import { flow, makeAutoObservable } from 'mobx';

export const RELEASE_CHECK_INTERVAL_IN_MILLISECONDS = 10 * 60 * 1000;

export const NEW_RELEASE_NOTIFICATION_MICRO_COPY = {
	TITLE: 'A Navina update is ready',
	DESCRIPTION: 'Save your changes, then refresh to use the latest version.',
	RELOAD: 'Refresh page',
	DISMISS: 'Dismiss',
} as const;

type GetDeployedReleaseId = () => Promise<string>;

export interface INewReleaseNotificationModel {
	readonly isNewReleaseAvailable: boolean;
	readonly reload: VoidFunction;
}

export interface NewReleaseNotificationModelParams {
	readonly runningReleaseId: string;
	readonly getDeployedReleaseId: GetDeployedReleaseId;
	readonly reloadPage: VoidFunction;
}

function* checkForNewReleaseFlowGenerator(
	this: NewReleaseNotificationModel,
): Generator<ReturnType<GetDeployedReleaseId>, void, Awaited<ReturnType<GetDeployedReleaseId>>> {
	const { getDeployedReleaseId, setDeployedReleaseId, setOngoingCheckForNewRelease } = this;

	try {
		const deployedReleaseId: Awaited<ReturnType<GetDeployedReleaseId>> = yield getDeployedReleaseId();
		setDeployedReleaseId(deployedReleaseId);
	} catch (error) {
		console.warn('Could not check for a new release', error);
	} finally {
		setOngoingCheckForNewRelease(null);
	}
}

export class NewReleaseNotificationModel implements INewReleaseNotificationModel {
	readonly runningReleaseId: string;
	readonly getDeployedReleaseId: GetDeployedReleaseId;
	readonly reloadPage: VoidFunction;

	maybeDeployedReleaseId: string | null = null;

	private constructor({ runningReleaseId, getDeployedReleaseId, reloadPage }: NewReleaseNotificationModelParams) {
		this.runningReleaseId = runningReleaseId;
		this.getDeployedReleaseId = getDeployedReleaseId;
		this.reloadPage = reloadPage;
		makeAutoObservable(this);
	}

	static readonly create = (params: NewReleaseNotificationModelParams): NewReleaseNotificationModel =>
		new NewReleaseNotificationModel(params);

	readonly setDeployedReleaseId = (deployedReleaseId: typeof this.maybeDeployedReleaseId): void => {
		this.maybeDeployedReleaseId = deployedReleaseId;
	};

	get isNewReleaseAvailable(): boolean {
		return this.maybeDeployedReleaseId !== null && this.maybeDeployedReleaseId !== this.runningReleaseId;
	}

	readonly reload = (): void => {
		this.reloadPage();
	};

	maybeOngoingCheckForNewRelease: Promise<void> | null = null;
	readonly setOngoingCheckForNewRelease = (ongoingCheck: typeof this.maybeOngoingCheckForNewRelease): void => {
		this.maybeOngoingCheckForNewRelease = ongoingCheck;
	};

	readonly checkForNewRelease = (): void => {
		if (this.maybeOngoingCheckForNewRelease) {
			return;
		}

		this.setOngoingCheckForNewRelease(this.checkForNewReleaseFlow());
	};

	readonly checkForNewReleaseFlow = flow(checkForNewReleaseFlowGenerator);

	readonly startWatching = (): VoidFunction => {
		const { checkForNewRelease } = this;

		function checkForNewReleaseWhenVisible(): void {
			if (document.visibilityState === 'visible') {
				checkForNewRelease();
			}
		}

		const intervalId = setInterval(checkForNewRelease, RELEASE_CHECK_INTERVAL_IN_MILLISECONDS);
		document.addEventListener('visibilitychange', checkForNewReleaseWhenVisible);
		globalThis.addEventListener('vite:preloadError', checkForNewRelease);

		return (): void => {
			clearInterval(intervalId);
			document.removeEventListener('visibilitychange', checkForNewReleaseWhenVisible);
			globalThis.removeEventListener('vite:preloadError', checkForNewRelease);
		};
	};
}
